import ApiGateway from '../api-gateway/api-gateway';
import UserSession from '@app/common/user-session';
import axios from 'axios';
import { tokenIsValid, triggerSecureLogout } from '@app/lib/utils';
import { cognitoUser } from '@app/lib/cognito';
import { store } from '@app/store';
import { actionCreators as authActionCreators } from '@app/common/auth/auth.reducer';

const getInfo = async () => {
  const state = store.getState();
  const partnerId = UserSession.getPartnerId();
  const expiration = state.common.auth.session?.expiration ?? 0;

  // Don't make API call if partnerId is undefined (user logged out)
  if (!partnerId) {
    console.log('Dashboard API: No partnerId available, user may be logged out');
    throw new Error('No partner ID available');
  }

  if (tokenIsValid(expiration)) {
    try {
      const result = await ApiGateway.get(`/partners/${partnerId}`);
      return result.data;
    } catch (error) {
      // Check if it's an authentication error and try to refresh token
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('invalid-jwt')) {
        try {
          console.log('Dashboard API: Authentication error detected, attempting token refresh...');
          store.dispatch(authActionCreators.tokenRefreshRequest());
          
          // Wait a moment for refresh to complete, then retry
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const retryResult = await ApiGateway.get(`/partners/${partnerId}`);
          console.log('Dashboard API: Token refresh and retry successful');
          return retryResult.data;
        } catch (retryError) {
          console.error('Dashboard API: Token refresh and retry failed:', retryError);

          triggerSecureLogout('Dashboard API: Token refresh failed after retry');
          throw retryError;
        }
      }
      throw error;
    }
  } else {
    // Token already expired, try refresh first instead of immediate logout
    console.log('Dashboard API: Token expired, attempting refresh...');
    try {
      store.dispatch(authActionCreators.tokenRefreshRequest());
      
      // Wait for refresh and retry
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const result = await ApiGateway.get(`/partners/${partnerId}`);
      console.log('Dashboard API: Token refresh successful, data retrieved');
      return result.data;
    } catch (error) {
      console.error('Dashboard API: Token refresh failed:', error);

      triggerSecureLogout('Dashboard API: Token refresh failed on expired token');
      return null;
    }
  }
};

const getStatusOnly = async () => {
  try {
    const response = await getInfo();
    
    const statusUpdates = [];
    
    // Extract site statuses
    if (response?.partner?.sites?.sites) {
      response.partner.sites.sites.forEach(site => {
        statusUpdates.push({
          id: site.id,
          type: 'site',
          status: site.status
        });
      });
    }
    
    // Extract controller statuses  
    if (response?.partner?.controllers?.controllers) {
      response.partner.controllers.controllers.forEach(controller => {
        statusUpdates.push({
          id: controller.id,
          type: 'controller', 
          status: controller.status
        });
      });
    }
    
    return statusUpdates;
  } catch (error) {
    throw error;
  }
};

export default {
  async getAll() {
    // Skip metrics requests
    return { sites: { metrics: {} } };
  },

  getInfo,
  getStatusOnly,
}
